import { memo, useMemo } from 'react'
import { cn } from '../../lib/utils'

// ── Types ────────────────────────────────────────────────────
export interface RotatingMillBarData {
  angle: number
  status: 'operational' | 'warning' | 'critical'
  m_data: number | null
}

export interface RotatingMillCrossSectionProps {
  rpm?: number | null
  isActive?: boolean
  bars?: RotatingMillBarData[]
  barCount?: number
  size?: number
  className?: string
}

// ── Constants (100x100 viewBox) ──────────────────────────────
const VB = 100
const CENTER = VB / 2 // 50
const OUTER_R = 40
const SHELL_T = 4
const INNER_R = OUTER_R - SHELL_T // 36
const MAX_BAR_H = 9
const MIN_BAR_H = 1
const BAR_BASE_W = 3.2
const BAR_TIP_W = 1.8

// ── Status colors ────────────────────────────────────────────
const STATUS_COLORS: Record<string, [string, string]> = {
  operational: ['#4ade80', '#16a34a'],
  warning: ['#fbbf24', '#d97706'],
  critical: ['#f87171', '#dc2626'],
  nodata: ['#9ca3af', '#6b7280'],
}

// ── Helpers ──────────────────────────────────────────────────

function getBarHeight(mData: number | null): number {
  if (mData == null) return MAX_BAR_H * 0.7
  return MAX_BAR_H - (mData / 100) * (MAX_BAR_H - MIN_BAR_H)
}

function getAnimationDuration(rpm: number | null | undefined): number {
  if (rpm == null || rpm <= 0) return 20 // very slow default
  // Real SAG/AG mills run ~8-15 RPM. Map to visual speed.
  // Lower duration = faster spin. Clamp between 1s and 30s.
  const duration = 60 / rpm // seconds per revolution
  return Math.max(1, Math.min(30, duration))
}

function buildTrapezoid(
  angleRad: number,
  barHeight: number,
  scaleFactor: number,
): string {
  const baseHalf = (BAR_BASE_W * scaleFactor) / 2
  const tipHalf = (BAR_TIP_W * scaleFactor) / 2

  const baseR = INNER_R
  const tipR = INNER_R - barHeight

  const cos = Math.cos(angleRad)
  const sin = Math.sin(angleRad)
  const perpCos = Math.cos(angleRad + Math.PI / 2)
  const perpSin = Math.sin(angleRad + Math.PI / 2)

  const bx = CENTER + cos * baseR
  const by = CENTER + sin * baseR
  const tx = CENTER + cos * tipR
  const ty = CENTER + sin * tipR

  return [
    [bx - perpCos * baseHalf, by - perpSin * baseHalf],
    [bx + perpCos * baseHalf, by + perpSin * baseHalf],
    [tx + perpCos * tipHalf, ty + perpSin * tipHalf],
    [tx - perpCos * tipHalf, ty - perpSin * tipHalf],
  ]
    .map(([x, y]) => `${x},${y}`)
    .join(' ')
}

function generateEvenBars(count: number): RotatingMillBarData[] {
  return Array.from({ length: count }, (_, i) => ({
    angle: (360 / count) * i,
    status: 'operational' as const,
    m_data: null,
  }))
}

// ── Component ────────────────────────────────────────────────
export const RotatingMillCrossSection = memo(function RotatingMillCrossSection({
  rpm,
  isActive = true,
  bars,
  barCount = 24,
  size = 250,
  className,
}: RotatingMillCrossSectionProps) {
  const resolvedBars = bars && bars.length > 0 ? bars : generateEvenBars(barCount)
  const count = resolvedBars.length
  const scaleFactor = count > 30 ? 30 / count : 1
  const duration = getAnimationDuration(rpm)

  const barPolygons = useMemo(
    () =>
      resolvedBars.map((bar) => {
        const angleRad = (bar.angle * Math.PI) / 180
        const barHeight = getBarHeight(bar.m_data)
        const gradientKey = bar.m_data == null ? 'nodata' : bar.status
        return {
          points: buildTrapezoid(angleRad, barHeight, scaleFactor),
          gradientKey,
          opacity: bar.m_data == null ? 0.5 : 0.85,
        }
      }),
    [resolvedBars, scaleFactor],
  )

  const shellPath = `
    M ${CENTER + OUTER_R},${CENTER}
    A ${OUTER_R},${OUTER_R} 0 1,1 ${CENTER - OUTER_R},${CENTER}
    A ${OUTER_R},${OUTER_R} 0 1,1 ${CENTER + OUTER_R},${CENTER}
    Z
    M ${CENTER + INNER_R},${CENTER}
    A ${INNER_R},${INNER_R} 0 1,0 ${CENTER - INNER_R},${CENTER}
    A ${INNER_R},${INNER_R} 0 1,0 ${CENTER + INNER_R},${CENTER}
    Z
  `

  return (
    <div
      className={cn(
        'inline-flex items-center justify-center',
        !isActive && 'opacity-50',
        className,
      )}
      style={{
        width: size,
        height: size,
        willChange: isActive ? 'transform' : undefined,
        animation: isActive ? `rmcs-spin ${duration}s linear infinite` : undefined,
      }}
    >
      <style>{`
        @keyframes rmcs-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="rmcs-spin"] {
            animation: none !important;
          }
        }
      `}</style>

      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${VB} ${VB}`}
        className="block"
        role="img"
        aria-label="Mill cross-section"
      >
        <defs>
          {/* Shell gradient */}
          <radialGradient id="rmcs-shell" cx="50%" cy="50%" r="50%">
            <stop offset="0%" className="[stop-color:theme(colors.gray.400)] dark:[stop-color:theme(colors.gray.500)]" />
            <stop offset="60%" className="[stop-color:theme(colors.gray.500)] dark:[stop-color:theme(colors.gray.600)]" />
            <stop offset="100%" className="[stop-color:theme(colors.gray.600)] dark:[stop-color:theme(colors.gray.700)]" />
          </radialGradient>

          {/* Status gradients */}
          {Object.entries(STATUS_COLORS).map(([key, [light, dark]]) => (
            <linearGradient key={key} id={`rmcs-${key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={light} />
              <stop offset="100%" stopColor={dark} />
            </linearGradient>
          ))}
        </defs>

        {/* Steel shell ring */}
        <path
          d={shellPath}
          fill="url(#rmcs-shell)"
          fillRule="evenodd"
          className="stroke-gray-700 dark:stroke-gray-500"
          strokeWidth="0.3"
        />

        {/* Lifter bars */}
        {barPolygons.map((bar, i) => (
          <polygon
            key={i}
            points={bar.points}
            fill={`url(#rmcs-${bar.gradientKey})`}
            className="stroke-gray-700 dark:stroke-gray-500"
            strokeWidth="0.2"
            opacity={bar.opacity}
          />
        ))}
      </svg>
    </div>
  )
})
